import { z } from '@/libs/zod';
import { parseDigits, validatePixKey, type PixKeyType } from '@/utils';
import { removeDDIFromAutoInsert } from '@/utils/formatters';

const normalizePixKey = (pixKeyType: PixKeyType, pixKey: string) =>
  pixKeyType === 'phone' ? removeDDIFromAutoInsert(parseDigits(pixKey)) : pixKey;

const pixKeySchema = z
  .object({
    pixKeyType: z.enum(['cpf', 'phone', 'email', 'random']),
    pixKey: z.string().min(1, 'Campo obrigatório'),
  })
  .superRefine((data, ctx) => {
    const error = validatePixKey(
      data.pixKeyType,
      normalizePixKey(data.pixKeyType, data.pixKey ?? ''),
    );

    if (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: error,
        path: ['pixKey'],
      });
    }
  });

const withdrawAddressSchema = z.object({
  zipCode: z
    .string()
    .trim()
    .min(8, 'Campo obrigatório')
    .refine((val) => parseDigits(val).length === 8, 'CEP deve conter 8 dígitos'),
  state: z.string().trim().min(2, 'Campo obrigatório').toUpperCase(),
  city: z
    .string()
    .trim()
    .min(3, 'Campo obrigatório')
    .regex(/^[\p{L}\p{N} ,.\-/'()]+$/u, 'Caracteres inválidos'),
  neighborhood: z
    .string()
    .trim()
    .min(3, 'Campo obrigatório')
    .regex(/^[\p{L}\p{N} ,.\-/'()]+$/u, 'Caracteres inválidos'),
  street: z
    .string()
    .trim()
    .min(3, 'Campo obrigatório')
    .refine((val) => !val.includes(':'), 'O logradouro não pode conter ":"'),
  number: z
    .string()
    .trim()
    .min(1, 'Informe um número válido ou "S/N"')
    .refine(
      (val) => {
        const isNumeric = /^\d+$/.test(val);
        const isSN = /^s\/n$/i.test(val) || /^sn$/i.test(val) || /^SN$/i.test(val);
        return isNumeric || isSN;
      },
      {
        message: 'Informe um número válido ou "S/N"',
      },
    ),
  complement: z.string().trim().optional(),
});

export const walletWithdrawRequest = z
  .intersection(pixKeySchema, withdrawAddressSchema)
  .transform((data) => ({
    ...data,
    pixKey: normalizePixKey(data.pixKeyType, data.pixKey),
  }));

type WalletWithdrawRequestShape = z.infer<typeof walletWithdrawRequest>;

export type WalletWithdrawRequest = {
  [K in keyof WalletWithdrawRequestShape]: WalletWithdrawRequestShape[K];
};
